// src/components/tutor/session_logs/SessionPlanMiniCard.js
//
// Compact, READ-ONLY plan progress summary for the read-only session log view
// (`ReadOnlyView` in TutorSessionLogModal). Shows every plan that covers the
// session's subject — overall progress, hours, and a per-subject breakdown — with
// a "View full plan" button that opens the student's Plan tab in a new tab.
//
// Deliberately lighter than SatPlanPrepCard: no per-item rows, no guidance, no
// progress recording. Renders nothing when the student has no matching plan.

import React, { useEffect, useMemo, useState } from "react";
import { IoSparklesSharp } from "react-icons/io5";
import { FiExternalLink } from "react-icons/fi";
import {
  fetchPlansForStudentDir,
  summarizePlanProgress,
  derivePlanDisplayStatus,
  itemProgressPct,
} from "../../../utils/sat/satPlanData";
import { planSubjectOrder, planTypeOf, PLAN_TYPES } from "../../../utils/plan/planConfig";
import { planMatchesSessionSubject, preferActivePlans } from "../../../utils/plan/planMatch";
import ProgressGauge from "../../sat/ProgressGauge";
import { colorForSubject } from "../../sat/HourDonut";

const STATUS = {
  active: { label: "Active", bg: "#dcfce7", color: "#166534" },
  completed: { label: "Completed", bg: "#dbeafe", color: "#1e40af" },
  draft: { label: "Draft", bg: "#f1f5f9", color: "#475569" },
  archived: { label: "Archived", bg: "#f1f5f9", color: "#94a3b8" },
};

export default function SessionPlanMiniCard({ studentDirId, sessionSubject }) {
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const plans = await fetchPlansForStudentDir(studentDirId);
        if (alive) setAllPlans(plans || []);
      } catch {
        if (alive) setAllPlans([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [studentDirId]);

  // Every plan that covers the session's subject, active ones first. A student
  // can have several active plans as long as their subjects differ.
  const matching = useMemo(
    () =>
      preferActivePlans(
        (allPlans || [])
          .filter((p) => p.status !== "archived")
          .filter((p) => (p.checklist || []).some((i) => !i.isStretch))
          .filter((p) => planMatchesSessionSubject(p, sessionSubject))
      ).sort((a, b) => (a.status === "active" ? -1 : 0) - (b.status === "active" ? -1 : 0)),
    [allPlans, sessionSubject]
  );

  if (loading || !matching.length) return null;

  return (
    <div className="slw-ro-ai-section">
      <div className="slw-ro-section-head">
        <IoSparklesSharp size={13} />
        <span>Plan Progress</span>
      </div>
      {matching.map((plan) => (
        <MiniPlanCard key={plan.id} plan={plan} studentDirId={studentDirId} />
      ))}
    </div>
  );
}

function MiniPlanCard({ plan, studentDirId }) {
  const type = planTypeOf(plan);
  const label = PLAN_TYPES[type].label;
  const badge = STATUS[derivePlanDisplayStatus(plan)] || STATUS.draft;
  const progress = summarizePlanProgress(plan);

  const subjects = planSubjectOrder(plan).map((subject, i) => {
    const items = (plan.checklist || []).filter((it) => !it.isStretch && it.subject === subject);
    const planned = items.reduce((a, b) => a + (Number(b.allocatedHours) || 0), 0);
    const covered = items.reduce((a, b) => a + (Number(b.coveredHours) || 0), 0);
    const pct =
      planned > 0
        ? Math.round(items.reduce((a, b) => a + (Number(b.allocatedHours) || 0) * itemProgressPct(b), 0) / planned)
        : 0;
    return { subject, planned, covered, pct, color: colorForSubject(subject, i) };
  });

  const openFullPlan = () => {
    const dir = plan.studentDirId || studentDirId;
    if (!dir) return;
    const q = plan.id ? `?plan=${encodeURIComponent(plan.id)}` : "";
    window.open(`/students/directory/${encodeURIComponent(dir)}/plan${q}`, "_blank", "noopener");
  };

  return (
    <div className="slw-ro-plan-card">
      <div className="slw-ro-plan-head">
        <div className="slw-ro-plan-title">
          <strong>{plan.title || `${label} Plan`}</strong>
          <span className="slw-ro-plan-pill" style={{ background: badge.bg, color: badge.color }}>
            {badge.label}
          </span>
          <span className="slw-ro-plan-pill slw-ro-plan-pill--muted">{plan.totalHours || 0}h planned</span>
          {plan.targetScore != null && (
            <span className="slw-ro-plan-pill slw-ro-plan-pill--muted">
              Target {plan.currentScore ?? plan.totalScore ?? "—"} → {plan.targetScore}
            </span>
          )}
        </div>
        <button type="button" className="slw-ro-plan-link" onClick={openFullPlan}>
          <FiExternalLink size={11} /> View full plan
        </button>
      </div>

      <div className="slw-ro-plan-body">
        <div className="slw-ro-plan-gauge">
          <ProgressGauge percent={progress.percent} size={56} stroke={7} />
          <div>
            <div className="slw-ro-plan-gauge-main">
              {progress.doneCount}/{progress.requiredCount} items done
            </div>
            <div className="slw-ro-plan-gauge-sub">
              {progress.coveredHours}/{progress.plannedHours}h worked · {progress.percent}%
            </div>
          </div>
        </div>

        <div className="slw-ro-plan-subjects">
          {subjects.map((s) => (
            <div key={s.subject} className="slw-ro-plan-subject">
              <span className="slw-ro-plan-subject-name" title={s.subject}>
                {s.subject}
              </span>
              <span className="slw-ro-plan-bar">
                <span className="slw-ro-plan-bar-inner" style={{ width: `${s.pct}%`, background: s.color }} />
              </span>
              <span className="slw-ro-plan-subject-meta">
                {s.covered}/{s.planned}h
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
